import { useEffect, useRef, useState } from "react";
import { useSceneStore, useTemporalStore } from "../store/sceneStore";
import { useFileImport } from "../hooks/useFileImport";
import { CheckIcon } from "./icons";
import type { ViewPreset } from "../types";

type MenuKey = "file" | "edit" | "view" | null;

export function MenuBar() {
  const [openMenu, setOpenMenu] = useState<MenuKey>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const importFiles = useFileImport();

  const objectCount = useSceneStore((s) => s.objects.length);
  const resetScene = useSceneStore((s) => s.resetScene);
  const requestView = useSceneStore((s) => s.requestView);
  const settings = useSceneStore((s) => s.settings);
  const toggleGround = useSceneStore((s) => s.toggleGround);
  const toggleGrid = useSceneStore((s) => s.toggleGrid);
  const toggleAutoRotate = useSceneStore((s) => s.toggleAutoRotate);

  const undo = useTemporalStore((s) => s.undo);
  const redo = useTemporalStore((s) => s.redo);
  const pastLength = useTemporalStore((s) => s.pastStates.length);
  const futureLength = useTemporalStore((s) => s.futureStates.length);

  useEffect(() => {
    if (!openMenu) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenu]);

  const toggle = (key: MenuKey) => setOpenMenu((prev) => (prev === key ? null : key));
  const runAndClose = (fn: () => void) => {
    fn();
    setOpenMenu(null);
  };
  const handleView = (preset: ViewPreset) => runAndClose(() => requestView(preset));

  return (
    <div className="menubar" ref={rootRef}>
      <span className="menubar-brand">CUBEO</span>

      <div className="menu">
        <button className={`menu-label ${openMenu === "file" ? "open" : ""}`} onClick={() => toggle("file")}>
          Datei
        </button>
        {openMenu === "file" && (
          <div className="menu-dropdown">
            <button
              onClick={() => runAndClose(() => window.confirm("Aktuelle Szene wirklich löschen?") && resetScene())}
              disabled={objectCount === 0}
            >
              Neue Szene
            </button>
            <button onClick={() => runAndClose(() => inputRef.current?.click())}>Importieren…</button>
          </div>
        )}
      </div>

      <div className="menu">
        <button className={`menu-label ${openMenu === "edit" ? "open" : ""}`} onClick={() => toggle("edit")}>
          Bearbeiten
        </button>
        {openMenu === "edit" && (
          <div className="menu-dropdown">
            <button disabled={pastLength === 0} onClick={() => runAndClose(() => undo())}>
              Rückgängig
            </button>
            <button disabled={futureLength === 0} onClick={() => runAndClose(() => redo())}>
              Wiederholen
            </button>
          </div>
        )}
      </div>

      <div className="menu">
        <button className={`menu-label ${openMenu === "view" ? "open" : ""}`} onClick={() => toggle("view")}>
          Ansicht
        </button>
        {openMenu === "view" && (
          <div className="menu-dropdown">
            <button onClick={() => handleView("front")}>Front</button>
            <button onClick={() => handleView("top")}>Oben</button>
            <button onClick={() => handleView("iso")}>Iso</button>
            <div className="menu-separator" />
            <button onClick={() => runAndClose(toggleGround)}>
              <CheckMark show={settings.groundVisible} /> Boden
            </button>
            <button onClick={() => runAndClose(toggleGrid)}>
              <CheckMark show={settings.gridVisible} /> Gitter
            </button>
            <button onClick={() => runAndClose(toggleAutoRotate)}>
              <CheckMark show={settings.autoRotate} /> Auto-Drehung
            </button>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".stl,.obj"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) importFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <span className="menubar-spacer" />
    </div>
  );
}

function CheckMark({ show }: { show: boolean }) {
  return <span className="menu-check">{show ? <CheckIcon size={13} /> : null}</span>;
}
